import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CALCULATOR_SIGNUP_PREFILL_STORAGE_KEY, ROUTES } from '@/constants';
import { Calculate, RocketLaunch } from '@mui/icons-material';
import { apiService } from '@services/api';
import {
  formatCurrency as formatCurrencyValue,
  calculateClientPrice as calculateClientPriceFromConfig,
  calculateClientPriceBreakdown,
  calculateVolumeDiscount as calculateVolumeDiscountFromConfig,
  calculateCapacityCost,
} from './unifiedPricingCalculations';
import { calculateExtraUsersCost as calculateExtraUsersCostFromPricing } from './config/generalConfigCalculations';
import ResponsiveDialogActions from '@components/ResponsiveDialogActions';
import { getMinPlanCapacity, normalizePlanPacks } from '@utils/planPacks';
import PlanPackageSelector from '@components/pricing/calculator/PlanPackageSelector';
import BasePriceExplanationCard from '@components/pricing/calculator/BasePriceExplanationCard';
import InvestmentTotalSummary from '@components/pricing/calculator/InvestmentTotalSummary';
import GestaoCapacitySelector from '@components/pricing/calculator/GestaoCapacitySelector';
import InstitucionalNucleusSelector from '@components/pricing/calculator/InstitucionalNucleusSelector';
import CondominioClientsList from '@components/pricing/calculator/CondominioClientsList';
import InstitucionalUsersSelector from '@components/pricing/calculator/InstitucionalUsersSelector';
import PriceTiersDisplay from '@components/pricing/calculator/PriceTiersDisplay';
import type { AvailablePackage, ClientInput, PlanKey } from '@components/pricing/calculator/pricingCalculatorTypes';

interface PricingConfig {
  _id?: string;
  /** Estrutura atual: tenantConfigs + availablePackages; root pode estar vazio */
  pricingModel?: 'unit-based' | 'tiered' | 'hybrid' | 'plan-based';
  basePrice?: number;
  minPricePerClient?: number;
  currency?: string;
  pricePerUnit?: number;
  priceTiers?: Array<{
    minUnits: number;
    maxUnits?: number | null;
    pricePerUnit: number;
  }>;
  effectivePriceTiers?: Array<{
    minUnits: number;
    maxUnits?: number | null;
    pricePerUnit: number;
    isMinimum: boolean;
  }>;
  volumeDiscounts?: Array<{
    minTotalUnits: number;
    discountPercentage: number;
  }>;
  billingCycle?: 'monthly' | 'quarterly' | 'annual';
  isActive?: boolean;
  includedUsers?: number;
  extraUserPricing?: {
    individualPrice: number;
    packages: Array<{ quantity: number; price: number }>;
  };
  availablePackages?: AvailablePackage[];
}

interface UnifiedPricingCalculatorProps {
  /** Se true, renderiza como Dialog (modal), senão renderiza como Paper (inline) */
  asDialog?: boolean;
  /** Se asDialog=true, controla se o dialog está aberto */
  open?: boolean;
  /** Se asDialog=true, callback quando o dialog é fechado */
  onClose?: () => void;
  /** Se true, permite múltiplos clientes, senão apenas um cliente */
  allowMultipleClients?: boolean;
  /** Quando definido, exibe apenas o pacote informado (ex.: página de clientes do tenant logado) */
  lockedPackageType?: 'condominio' | 'gestao' | 'institucional';
  /** Valores iniciais quando aberto na página de clientes (plano vigente) */
  initialClientCount?: number;
  initialNucleusCount?: number;
  initialExtraUsersCount?: number;
}

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
const UnifiedPricingCalculator: React.FC<UnifiedPricingCalculatorProps> = ({
  asDialog = false,
  open = false,
  onClose,
  allowMultipleClients = true,
  lockedPackageType,
  initialClientCount,
  initialNucleusCount,
  initialExtraUsersCount,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackageType, setSelectedPackageType] = useState<PlanKey | null>(() => {
    if (lockedPackageType) return lockedPackageType;
    if (!asDialog) return 'condominio';
    return null;
  });
  const [clients, setClients] = useState<ClientInput[]>([
    { id: '1', units: '' }
  ]);
  const [clientCount, setClientCount] = useState<number>(0); // Para pacote gestão (condomínios)
  const [nucleusCount, setNucleusCount] = useState<number>(0); // Para pacote institucional (núcleos)
  const [extraUsersCount, setExtraUsersCount] = useState<number>(0);
  const [customUserCount, setCustomUserCount] = useState<number>(0);
  const [useCustomCount, setUseCustomCount] = useState(false);
  const availablePackages = React.useMemo<AvailablePackage[]>(
    () => {
      const list = pricingConfig?.availablePackages ?? [];
      // Em calculadoras públicas (landing/signup), esconder planos desativados.
      // Em contexto "lockedPackageType" (tenant logado), mantém mesmo se foi desativado depois.
      return lockedPackageType ? list : list.filter((p) => p?.enabled !== false);
    },
    [pricingConfig?.availablePackages, lockedPackageType]
  );
  // Gestão pode vir no banco como key 'gestao' ou 'operador' (legado). Sem plano escolhido (landing): sem fallback para [0].
  const selectedPackage =
    selectedPackageType == null
      ? undefined
      : availablePackages.find((p) => (p.key || '').toLowerCase() === selectedPackageType) ??
        (selectedPackageType === 'gestao'
          ? availablePackages.find((p) => (p.key || '').toLowerCase() === 'operador')
          : undefined);

  // Pacotes de capacidade do plano Gestão: do pacote (clientCapacityPacks ou condoCapacityPacks legado) ou packages.gestao
  const gestaoCapacityPacks = React.useMemo(() => {
    const pkg = selectedPackageType === 'gestao' ? selectedPackage : null;
    const fromPkg = pkg ? (pkg.clientCapacityPacks ?? pkg.condoCapacityPacks) : undefined;
    if (Array.isArray(fromPkg) && fromPkg.length > 0) return normalizePlanPacks(fromPkg);
    const fromRoot = (pricingConfig as unknown as Record<string, unknown>)?.packages as Record<string, { clientCapacityPacks?: Array<{ capacity: number; price: number }>; condoCapacityPacks?: Array<{ capacity: number; price: number }> }> | undefined;
    const gestaoPkg = fromRoot?.gestao ?? fromRoot?.operador;
    return normalizePlanPacks(gestaoPkg?.clientCapacityPacks ?? gestaoPkg?.condoCapacityPacks);
  }, [selectedPackageType, selectedPackage, pricingConfig]);

  const minGestaoCapacity = React.useMemo(
    () => getMinPlanCapacity(gestaoCapacityPacks),
    [gestaoCapacityPacks]
  );

  // Config efetiva: do pacote selecionado ou fallback para config flat
  const effectiveConfig = selectedPackage ? {
    // Novo modelo: Condomínio e Gestão não têm taxa base nem usuários extras.
    basePrice: selectedPackageType === 'institucional'
      ? (selectedPackage.basePrice ?? pricingConfig?.basePrice ?? 99)
      : 0,
    includedUsers: selectedPackageType === 'institucional'
      ? (selectedPackage.includedUsers ?? pricingConfig?.includedUsers ?? 3)
      : 0,
    extraUserPricing: selectedPackageType === 'institucional'
      ? (selectedPackage.extraUserPricing ?? pricingConfig?.extraUserPricing)
      : undefined,
    priceTiers: selectedPackage.priceTiers ?? pricingConfig?.priceTiers ?? [],
    volumeDiscounts: selectedPackage.volumeDiscounts ?? pricingConfig?.volumeDiscounts ?? [],
    minPricePerClient: selectedPackage.minPricePerClient ?? pricingConfig?.minPricePerClient ?? 0,
    pricingModel: (selectedPackage.pricingModel ?? pricingConfig?.pricingModel ?? 'hybrid') as 'hybrid' | 'unit-based' | 'tiered' | 'plan-based',
    pricePerUnit: pricingConfig?.pricePerUnit ?? 1,
    clientCapacityPacks: selectedPackageType === 'gestao' ? gestaoCapacityPacks : (selectedPackage.clientCapacityPacks ?? selectedPackage.condoCapacityPacks ?? []),
    nucleusCapacityPacks: selectedPackage.nucleusCapacityPacks ?? [],
  } : {
    basePrice: 0,
    includedUsers: 0,
    extraUserPricing: undefined,
    priceTiers: pricingConfig?.priceTiers ?? [],
    volumeDiscounts: pricingConfig?.volumeDiscounts ?? [],
    minPricePerClient: pricingConfig?.minPricePerClient ?? 0,
    pricingModel: (pricingConfig?.pricingModel ?? 'hybrid') as 'hybrid' | 'unit-based' | 'tiered' | 'plan-based',
    pricePerUnit: pricingConfig?.pricePerUnit ?? 1,
    clientCapacityPacks: selectedPackageType === 'gestao' ? gestaoCapacityPacks : ([] as Array<{ capacity: number; price: number }>),
    nucleusCapacityPacks: [] as Array<{ capacity: number; price: number }>,
  };

  const isGestao = selectedPackageType === 'gestao';
  const isInstitucional = selectedPackageType === 'institucional';

  useEffect(() => {
    loadPricingConfig();
  }, []);

  useEffect(() => {
    if (availablePackages.length !== 1) return;
    const pkg = availablePackages[0];
    if (!pkg) return;
    const key = (pkg.key || '').toLowerCase();
    if (key === 'operador') {
      setSelectedPackageType('gestao');
    } else if (key === 'condominio' || key === 'gestao' || key === 'institucional') {
      setSelectedPackageType(key as PlanKey);
    }
  }, [availablePackages]);

  useEffect(() => {
    if (asDialog && !open) {
      setClients([{ id: '1', units: '' }]);
      setClientCount(0);
      setNucleusCount(0);
      setExtraUsersCount(0);
      setCustomUserCount(0);
      setUseCustomCount(false);
      // Landing (sem lockedPackageType): sempre volta sem plano — evita pré-selecionar Condomínio no mount/fechar.
      setSelectedPackageType(lockedPackageType ?? null);
    }
  }, [asDialog, open, lockedPackageType]);

  useEffect(() => {
    if (asDialog && open && (initialClientCount != null || initialNucleusCount != null || initialExtraUsersCount != null)) {
      setSelectedPackageType(lockedPackageType ?? null);
      if (initialClientCount != null && initialClientCount > 0) setClientCount(initialClientCount);
      if (initialNucleusCount != null && initialNucleusCount > 0) setNucleusCount(initialNucleusCount);
      if (initialExtraUsersCount != null && initialExtraUsersCount > 0) setExtraUsersCount(initialExtraUsersCount);
    }
  }, [asDialog, open, lockedPackageType, initialClientCount, initialNucleusCount, initialExtraUsersCount]);

  useEffect(() => {
    if (lockedPackageType && (lockedPackageType === 'condominio' || lockedPackageType === 'gestao' || lockedPackageType === 'institucional')) {
      setSelectedPackageType(lockedPackageType);
    }
  }, [lockedPackageType]);

  const loadPricingConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPricingConfig();
      if (response.data.success) {
        setPricingConfig(response.data.data);
      }
    } catch {
      setError('Erro ao carregar configuração de preços');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = formatCurrencyValue;

  const calculateClientPrice = (units: number): number =>
    calculateClientPriceFromConfig(units, effectiveConfig);

  const calculateExtraUsersCost = (extraUsers: number): { extraUsers: number; cost: number } => ({
    extraUsers,
    cost: calculateExtraUsersCostFromPricing(extraUsers, effectiveConfig?.extraUserPricing),
  });

  const calculateVolumeDiscount = (totalUnits: number, subtotal: number): number =>
    calculateVolumeDiscountFromConfig(totalUnits, subtotal, effectiveConfig, isGestao || isInstitucional);

  /** Gestão: pacote pré-definido cobra o preço do pacote; outra quantidade = count × unitário do maior pacote abaixo dessa quantidade (se não houver, usa o menor pacote). */
  const calculateClientCapacityCost = (count: number): number =>
    calculateCapacityCost(count, effectiveConfig?.clientCapacityPacks);

  /** Para pacote institucional: custo pelo número de núcleos (mesma regra de capacidade) */
  const calculateNucleusCapacityCost = (count: number): number =>
    calculateCapacityCost(count, effectiveConfig?.nucleusCapacityPacks);

  const handleUnitsChange = (clientId: string, value: string) => {
    const numericValue = value.replace(/\D/g, '');
    setClients(prev => prev.map(client => 
      client.id === clientId ? { ...client, units: numericValue } : client
    ));
  };

  const handleAddClient = () => {
    const newId = String(Date.now());
    setClients(prev => [...prev, { id: newId, units: '' }]);
  };

  const handleRemoveClient = (clientId: string) => {
    if (clients.length > 1) {
      setClients(prev => prev.filter(client => client.id !== clientId));
    }
  };

  const handleClose = () => {
    setClients([{ id: '1', units: '' }]);
    if (onClose) {
      onClose();
    }
  };

  // Calcular valores
  const clientPrices = clients.map((client) => {
    const units = parseInt(client.units, 10) || 0;
    return calculateClientPriceBreakdown(units, effectiveConfig);
  });

  const totalUnits = clientPrices.reduce((sum, item) => sum + item.units, 0);
  const subtotal = clientPrices.reduce((sum, item) => sum + item.price, 0);
  const minPricePerClient = effectiveConfig?.minPricePerClient || 0;
  const minimumsSubtotal = clientPrices.reduce((sum, item) => sum + item.minimumApplied, 0);
  const variableSubtotal = Math.max(0, subtotal - minimumsSubtotal);
  const volumeDiscount = calculateVolumeDiscount(totalUnits, variableSubtotal);
  const totalClientsPrice = subtotal - volumeDiscount;
  const basePrice = effectiveConfig?.basePrice || 0;
  const { extraUsers, cost: extraUsersCost } = calculateExtraUsersCost(extraUsersCount);

  const clientCapacityCost = isGestao ? calculateClientCapacityCost(clientCount) : 0;
  const nucleusCapacityCost = isInstitucional ? calculateNucleusCapacityCost(nucleusCount) : 0;

  const preFloorTotal = isGestao
    ? basePrice + clientCapacityCost + extraUsersCost
    : isInstitucional
      ? basePrice + nucleusCapacityCost + extraUsersCost
      : basePrice + totalClientsPrice + extraUsersCost;
  const totalMonthly = Math.max(preFloorTotal, basePrice);

  const showCalculatorForm = selectedPackageType != null;

  const contractQuoteReady =
    showCalculatorForm &&
    ((!isGestao && !isInstitucional && totalUnits > 0) ||
      (isGestao && clientCount >= minGestaoCapacity) ||
      (isInstitucional && nucleusCount > 0));

  const handleContractNavigate = () => {
    if (!selectedPackageType || !contractQuoteReady) return;
    const prefill = {
      version: 1 as const,
      packageType: selectedPackageType,
      clientCapacity: isGestao ? clientCount : undefined,
      nucleusCapacity: isInstitucional ? nucleusCount : undefined,
      extraUsersCount: isInstitucional ? extraUsersCount : undefined,
      condominiumUnits:
        !isGestao && !isInstitucional
          ? clients.map((c) => parseInt(c.units, 10) || 0).filter((u) => u > 0)
          : undefined,
      totalMonthly,
    };
    try {
      sessionStorage.setItem(CALCULATOR_SIGNUP_PREFILL_STORAGE_KEY, JSON.stringify(prefill));
    } catch {
      // ignore
    }
    navigate(`${ROUTES.LOGIN}?tab=signup`);
    onClose?.();
  };

  const lockedPlanInfo = React.useMemo(() => {
    if (!lockedPackageType) return null;
    const key = lockedPackageType.toLowerCase();
    const ap =
      availablePackages.find((p) => (p.key || '').toLowerCase() === key) ??
      (key === 'gestao' ? availablePackages.find((p) => (p.key || '').toLowerCase() === 'operador') : null);
    const name = ap?.name || (key === 'gestao' ? 'VOVER Gestão' : key === 'institucional' ? 'VOVER Institucional' : 'VOVER Condomínio');
    const includedUsers = effectiveConfig?.includedUsers ?? 0;
    const basePriceInfo = effectiveConfig?.basePrice ?? 0;
    return { name, includedUsers, basePriceInfo };
  }, [lockedPackageType, availablePackages, effectiveConfig?.includedUsers, effectiveConfig?.basePrice]);

  const calculatorContent = (
    <Box sx={asDialog ? { pt: 2 } : { p: 4 }}>
      {!asDialog && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Calculate color="primary" />
          <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
            Calcule seu investimento
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error || !pricingConfig ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error || 'Configuração de preços não disponível'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {lockedPackageType && lockedPlanInfo && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 0 }}>
                <strong>Seu plano atual:</strong> {lockedPlanInfo.name}
                {lockedPlanInfo.basePriceInfo > 0 ? ` — taxa base ${formatCurrency(lockedPlanInfo.basePriceInfo)}` : ''}
                {lockedPlanInfo.includedUsers > 0 ? ` — ${lockedPlanInfo.includedUsers} usuários incluídos` : ''}
                {lockedPackageType === 'gestao' && typeof clientCount === 'number' && clientCount > 0
                  ? ` — capacidade contratada: ${clientCount} condomínios`
                  : ''}
                {lockedPackageType === 'institucional' && typeof nucleusCount === 'number' && nucleusCount > 0
                  ? ` — capacidade contratada: ${nucleusCount} núcleos`
                  : ''}
              </Alert>
            </Grid>
          )}

          {/* Seletor de Pacote - oculto quando lockedPackageType (ex.: página de clientes) */}
          {availablePackages.length >= 2 && !lockedPackageType && (
            <PlanPackageSelector
              availablePackages={availablePackages}
              selectedPackageType={selectedPackageType}
              asDialog={asDialog}
              onSelect={(planKey) => {
                setSelectedPackageType(planKey);
                setClients([{ id: '1', units: '' }]);
                setClientCount(0);
                setNucleusCount(0);
              }}
            />
          )}

          {showCalculatorForm && (
            <>
          {/* Explicação da Taxa Base - texto condizente com o plano */}
          {basePrice > 0 && (
            <BasePriceExplanationCard
              basePrice={basePrice}
              includedUsers={effectiveConfig?.includedUsers ?? 3}
              isGestao={isGestao}
              isInstitucional={isInstitucional}
              formatCurrency={formatCurrency}
            />
          )}

          {/* Investimento Mensal Total */}
          {((!isGestao && !isInstitucional && totalUnits > 0) || (isGestao && clientCount >= minGestaoCapacity) || (isInstitucional && nucleusCount > 0)) && (
            <InvestmentTotalSummary
              totalMonthly={totalMonthly}
              basePrice={basePrice}
              isGestao={isGestao}
              isInstitucional={isInstitucional}
              clientCount={clientCount}
              clientCapacityCost={clientCapacityCost}
              nucleusCount={nucleusCount}
              nucleusCapacityCost={nucleusCapacityCost}
              extraUsers={extraUsers}
              extraUsersCost={extraUsersCost}
              subtotal={subtotal}
              minPricePerClient={minPricePerClient}
              minimumsSubtotal={minimumsSubtotal}
              volumeDiscount={volumeDiscount}
              totalClientsPrice={totalClientsPrice}
              preFloorTotal={preFloorTotal}
              formatCurrency={formatCurrency}
            />
          )}

          {/* VCondo Planos — timeline + tabela comparativa */}
          {isGestao && gestaoCapacityPacks.length > 0 && (
            <Grid item xs={12}>
              <GestaoCapacitySelector
                gestaoCapacityPacks={gestaoCapacityPacks}
                clientCount={clientCount}
                onClientCountChange={setClientCount}
                minGestaoCapacity={minGestaoCapacity}
                calculateClientCapacityCost={calculateClientCapacityCost}
                formatCurrency={formatCurrency}
              />
            </Grid>
          )}

          {/* Quantidade de Núcleos - Pacote Institucional */}
          {isInstitucional && (
            <Grid item xs={12}>
              <InstitucionalNucleusSelector
                nucleusCapacityPacks={effectiveConfig?.nucleusCapacityPacks ?? []}
                nucleusCount={nucleusCount}
                onNucleusCountChange={setNucleusCount}
                calculateNucleusCapacityCost={calculateNucleusCapacityCost}
                formatCurrency={formatCurrency}
              />
            </Grid>
          )}

          {/* Lista de condomínios - Pacote Condomínio */}
          {!isGestao && !isInstitucional && (
            <CondominioClientsList
              clients={clients}
              allowMultipleClients={allowMultipleClients}
              calculateClientPrice={calculateClientPrice}
              formatCurrency={formatCurrency}
              onUnitsChange={handleUnitsChange}
              onAddClient={handleAddClient}
              onRemoveClient={handleRemoveClient}
            />
          )}

          {/* Seleção de Usuários */}
          {isInstitucional && (
            <Grid item xs={12}>
              <InstitucionalUsersSelector
                includedUsers={effectiveConfig?.includedUsers ?? 3}
                extraUsersCount={extraUsersCount}
                customUserCount={customUserCount}
                useCustomCount={useCustomCount}
                extraUserPricing={effectiveConfig?.extraUserPricing}
                onSelectIncluded={() => {
                  setExtraUsersCount(0);
                  setUseCustomCount(false);
                  setCustomUserCount(0);
                }}
                onSelectPackage={(quantity) => {
                  setExtraUsersCount(quantity);
                  setUseCustomCount(false);
                  setCustomUserCount(0);
                }}
                onCustomFocus={() => setUseCustomCount(true)}
                onCustomCountChange={(rawValue) => {
                  const value = parseInt(rawValue, 10);
                  if (!isNaN(value) && value >= 0) {
                    setCustomUserCount(value);
                    setExtraUsersCount(value);
                    setUseCustomCount(true);
                  } else if (rawValue === '') {
                    setCustomUserCount(0);
                    setExtraUsersCount(0);
                  }
                }}
                calculateExtraUsersCost={calculateExtraUsersCost}
              />
            </Grid>
          )}

          {/* Faixas de Preço (apenas Condomínio) */}
          {!isGestao && !isInstitucional && (
            <PriceTiersDisplay
              priceTiers={effectiveConfig.priceTiers ?? []}
              minPricePerClient={minPricePerClient}
              formatCurrency={formatCurrency}
            />
          )}

          {asDialog && !lockedPackageType && contractQuoteReady ? (
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                startIcon={<RocketLaunch />}
                onClick={handleContractNavigate}
                sx={{ py: 1.5, fontWeight: 'bold' }}
              >
                Contratar
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                Você será direcionado ao cadastro com o plano e valores preenchidos conforme a simulação.
              </Typography>
            </Grid>
          ) : null}

          {/* Descontos Progressivos */}
            </>
          )}
        </Grid>
      )}
    </Box>
  );

  if (asDialog) {
    return (
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: theme.shadows[8]
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calculate color="primary" />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              Calcule seu investimento
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {calculatorContent}
        </DialogContent>
        <ResponsiveDialogActions
          primaryActions={[
            {
              label: 'Fechar',
              onClick: handleClose,
              buttonProps: {
                variant: 'outlined',
                color: 'inherit',
              },
            },
          ]}
        />
      </Dialog>
    );
  }

  return (
    <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
      {calculatorContent}
    </Paper>
  );
};

export default UnifiedPricingCalculator;

